import { SITE_LAT, SITE_LON, SITE_TIMEZONE } from './site';

// Real solar position for the site, so the sun rises where it actually rises
// and shadows fall where they actually would on the day you are looking.
//
// NOAA low-precision algorithm: accurate to better than a degree, which is far
// below what a shadow at this scale can show.

const RAD = Math.PI / 180;

export interface SunAngles {
    /** Degrees above the horizon; negative after sunset. */
    elev: number;
    /** Compass degrees from true north, clockwise (90 = east, 180 = south). */
    azim: number;
}

export function sunAnglesAt(when: Date): SunAngles {
    // Days since the J2000.0 epoch.
    const d = when.getTime() / 86_400_000 + 2440587.5 - 2451545.0;
    const meanAnomaly = (357.529 + 0.98560028 * d) * RAD;
    const meanLongitude = (280.459 + 0.98564736 * d) * RAD;
    const eclipticLon =
        meanLongitude + (1.915 * Math.sin(meanAnomaly) + 0.02 * Math.sin(2 * meanAnomaly)) * RAD;
    const obliquity = (23.439 - 0.00000036 * d) * RAD;

    const rightAsc = Math.atan2(Math.cos(obliquity) * Math.sin(eclipticLon), Math.cos(eclipticLon));
    const decl = Math.asin(Math.sin(obliquity) * Math.sin(eclipticLon));

    // Greenwich mean sidereal time -> local hour angle.
    const gmstHours = (18.697374558 + 24.06570982441908 * d) % 24;
    const localSidereal = gmstHours * 15 * RAD + SITE_LON * RAD;
    const hourAngle = localSidereal - rightAsc;

    const lat = SITE_LAT * RAD;
    const elev = Math.asin(
        Math.sin(lat) * Math.sin(decl) + Math.cos(lat) * Math.cos(decl) * Math.cos(hourAngle),
    );
    const azim = Math.atan2(
        -Math.sin(hourAngle),
        Math.tan(decl) * Math.cos(lat) - Math.sin(lat) * Math.cos(hourAngle),
    );

    return { elev: elev / RAD, azim: (((azim / RAD) % 360) + 360) % 360 };
}

/** Minutes the site's timezone is offset from UTC right now (+180 for Riyadh). */
function siteOffsetMinutes(at: Date): number {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: SITE_TIMEZONE,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })
        .formatToParts(at)
        .reduce<Record<string, string>>((acc, p) => {
            if (p.type !== 'literal') acc[p.type] = p.value;
            return acc;
        }, {});
    const asUtc = Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour) % 24,
        Number(parts.minute),
        Number(parts.second),
    );
    return (asUtc - at.getTime()) / 60_000;
}

/** The time slider's 0..1 spans 05:00 -> 23:00 local time on today's date. */
export const SLIDER_START_HOUR = 5;
export const SLIDER_HOURS = 18;

export function sunAnglesForSlider(t: number): SunAngles {
    const now = new Date();
    const offset = siteOffsetMinutes(now);
    // Today's calendar date at the site.
    const local = new Date(now.getTime() + offset * 60_000);
    const hour = SLIDER_START_HOUR + t * SLIDER_HOURS;
    const utcMs =
        Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate(), 0, 0, 0) +
        hour * 3_600_000 -
        offset * 60_000;
    return sunAnglesAt(new Date(utcMs));
}

/** Slider position matching the site's wall clock right now. */
export function sliderForNow(): number {
    const now = new Date();
    const local = new Date(now.getTime() + siteOffsetMinutes(now) * 60_000);
    const hour = local.getUTCHours() + local.getUTCMinutes() / 60;
    return Math.min(Math.max((hour - SLIDER_START_HOUR) / SLIDER_HOURS, 0), 1);
}

/** The site's wall clock as HH:MM. */
export function siteClock(): string {
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: SITE_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(new Date());
}

/** Slider position formatted as the local time it represents. */
export function formatSliderTime(t: number): string {
    const hour = SLIDER_START_HOUR + t * SLIDER_HOURS;
    const h = Math.floor(hour);
    const min = Math.floor((hour - h) * 60);
    return `${String(h % 24).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}
